import { useState, type FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Modal } from '../../components/ui/Modal'
import { Field } from '../../components/ui/Field'
import { Input } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { linksApi } from '../../api/links.api'
import { toast } from '../../store/toastStore'
import { ApiError } from '../../api/client'
import type { ExternalLink } from '../../api/types'

export function ExternalLinkFormModal({ link, onClose }: { link?: ExternalLink | null; onClose: () => void }) {
  const isEdit = Boolean(link)
  const queryClient = useQueryClient()

  const [label, setLabel] = useState(link?.label ?? '')
  const [url, setUrl] = useState(link?.url ?? '')
  const [icon, setIcon] = useState(link?.icon ?? '')
  const [error, setError] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const saveMutation = useMutation({
    mutationFn: () =>
      isEdit && link ? linksApi.update(link.id, { label, url, icon }) : linksApi.create({ label, url, icon }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['links'] })
      toast.success(isEdit ? 'Link aktualisiert.' : 'Link angelegt.')
      onClose()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.'),
  })

  const deleteMutation = useMutation({
    mutationFn: () => linksApi.remove(link!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['links'] })
      toast.success('Link gelöscht.')
      onClose()
    },
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    saveMutation.mutate()
  }

  return (
    <Modal
      title={isEdit ? 'Link bearbeiten' : 'Neuer Link'}
      onClose={onClose}
      footer={
        <>
          {isEdit ? (
            <Button variant="danger" onClick={() => setConfirmDelete(true)} style={{ marginRight: 'auto' }}>
              Löschen
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={onSubmit} loading={saveMutation.isPending}>
            Speichern
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {error ? <div style={{ color: 'var(--danger)', fontSize: 'var(--text-sm)' }}>{error}</div> : null}
        <Field label="Name" htmlFor="lk-label">
          <Input id="lk-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="z.B. Pterodactyl-Panel" required autoFocus />
        </Field>
        <Field label="URL" htmlFor="lk-url">
          <Input id="lk-url" type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" required />
        </Field>
        <Field label="Icon (optional)" htmlFor="lk-icon" hint="Ein einzelnes Emoji, z.B. 🐉">
          <Input id="lk-icon" value={icon} onChange={(e) => setIcon(e.target.value)} maxLength={8} />
        </Field>
      </form>

      {confirmDelete ? (
        <ConfirmDialog
          title="Link löschen"
          message={`"${link?.label}" wirklich löschen?`}
          danger
          loading={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate()}
          onCancel={() => setConfirmDelete(false)}
        />
      ) : null}
    </Modal>
  )
}
